import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { formatApiErrorDetail } from '../lib/api';

/**
 * Handles the return trip from Emergent-managed Google sign-in.
 * Rendered whenever the current location hash contains `session_id=`.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const sessionId = params.get('session_id');

    (async () => {
      try {
        if (!sessionId) throw new Error('Missing sign-in session.');
        const { data } = await api.post('/auth/google/session', null, {
          headers: { 'X-Session-ID': sessionId },
        });
        setUser(data);
        window.history.replaceState({}, '', '/my-login');
        window.location.replace('/my-login');
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    })();
  }, [setUser]);

  return (
    <div className="w-full px-margin-mobile lg:px-margin py-space-3xl" data-testid="auth-callback">
      <div className="max-w-md mx-auto text-center flex flex-col items-center">
        {error ? (
          <>
            <span className="font-label-sm text-label-sm uppercase tracking-widest text-surface-tint font-bold mb-space-xs">
              Sign-in problem
            </span>
            <h1 className="font-headline-md text-headline-sm text-outline uppercase tracking-tight mb-space-md">
              We couldn’t finish signing you in
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant mb-space-lg" data-testid="auth-callback-error">
              {error}
            </p>
            <a
              href="/my-login"
              data-testid="auth-callback-retry"
              className="inline-flex items-center justify-center h-12 px-space-xl rounded-lg bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-wider"
            >
              Back to sign in
            </a>
          </>
        ) : (
          <>
            <span className="inline-block w-3 h-3 rounded-full bg-surface-tint animate-pulse mb-space-md" />
            <p className="font-body-md text-body-md text-on-surface-variant">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
}
